import {
  type AppRole,
  type AppRoleSearchResult,
  OrderType,
  useGetAppRolesLazyQuery,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import {
  type FormSearchMultipleSelectionProps,
  type OptionSelectionProps,
  FormSearchMultipleSelection,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';

interface UserAppRoleSelectionProps<T extends FieldValues>
  extends Omit<FormSearchMultipleSelectionProps<T>, 'options'> {
  helperText?: React.ReactNode;
  valueKey?: 'id' | 'name';
}

export function UserAppRoleSelection<T extends FieldValues>(props: UserAppRoleSelectionProps<T>) {
  const { name, helperText, label, placeholder, valueKey = 'name', ...restProps } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [appRolesMap, setAppRolesMap] = useState<OptionSelectionProps[]>([]);
  const [getAppRoles, { loading: isAppRoleLoading }] = useGetAppRolesLazyQuery({
    variables: {
      paging: { pageSize: 10000, currentPage: 0 }, // NOTED: get all = 10000
      order: { field: 'name', type: OrderType.Asc },
      search: {},
    },
    onCompleted: (res) => {
      const appRolesResult = (res?.getAppRoles ?? {}) as AppRoleSearchResult;
      const mapRoles: OptionSelectionProps[] = appRolesResult.roles.map((role: AppRole) => {
        return { label: role.name, value: valueKey === 'id' ? role.id : role.name };
      });
      setAppRolesMap(mapRoles);
    },
  });
  const handleOpenOptions = () => {
    getAppRoles();
    setIsOpen(true);
  };

  const noOptionsText = helperText ?? 'ไม่พบข้อมูล';

  return (
    <FormSearchMultipleSelection
      name={name}
      options={appRolesMap}
      placeholder={placeholder}
      loading={isAppRoleLoading}
      noOptionsText={noOptionsText}
      label={label}
      open={isOpen}
      onOpen={handleOpenOptions}
      onClose={() => setIsOpen(false)}
      {...restProps}
      sx={{
        width: '100%',
        ...restProps.sx,
      }}
      getOptionLabel={(option) => option?.label ?? ''}
    />
  );
}

export function UserAppRoleSelectionWithPermission<T extends FieldValues>(
  props: Omit<UserAppRoleSelectionProps<T>, 'helperText'>,
) {
  const hasPermission = useHasPermission([
    APP_PERMISSIONS.SETTING.ROLE.UPDATE,
    APP_PERMISSIONS.SETTING.USER.UPDATE,
  ]);

  return <UserAppRoleSelection {...props} disabled={!hasPermission} />;
}
